import React from 'react';
import styled, { keyframes } from 'styled-components';
import { ChefHat, Utensils, ArrowRight, Sparkles } from 'lucide-react';
import { theme } from '../styles/theme';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const sparkleAnimation = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.7; }
  50% { transform: scale(1.2) rotate(180deg); opacity: 1; }
`;

const WelcomeContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.gradient.luxury};
  background-size: 200% 200%;
  animation: luxuryGradient 25s ease infinite;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.xl};
  position: relative;
  overflow: hidden;
  
  /* 🌟 SOFISTIKE OVERLAY */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.colors.gradient.sophisticated};
    animation: elegantPulse 20s ease-in-out infinite;
    opacity: 0.3;
  }
  
  /* ✨ SUBTLE PARTICLES */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(1px 1px at 25px 40px, rgba(99, 102, 241, 0.4), transparent),
      radial-gradient(1px 1px at 80px 20px, rgba(139, 92, 246, 0.3), transparent),
      radial-gradient(2px 2px at 150px 80px, rgba(245, 158, 11, 0.3), transparent),
      radial-gradient(1px 1px at 200px 50px, rgba(236, 72, 153, 0.2), transparent);
    background-repeat: repeat;
    background-size: 250px 120px;
    animation: gentleFloat 30s linear infinite;
    opacity: 0.6;
  }
  
  @keyframes luxuryGradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @keyframes elegantPulse {
    0%, 100% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.01); }
  }
  
  @keyframes gentleFloat {
    0% { transform: translateY(0) rotate(0deg); opacity: 0.6; }
    33% { transform: translateY(-5px) rotate(120deg); opacity: 0.8; }
    66% { transform: translateY(3px) rotate(240deg); opacity: 0.7; }
    100% { transform: translateY(0) rotate(360deg); opacity: 0.6; }
  }
`;

const BackgroundPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image: radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
                    radial-gradient(circle at 75% 75%, white 2px, transparent 2px);
  background-size: 50px 50px;
  background-position: 0 0, 25px 25px;
`;

const WelcomeCard = styled.div`
  background: ${theme.colors.glass.background};
  backdrop-filter: ${theme.colors.glass.backdrop};
  border: 2px solid transparent;
  border-radius: 32px;
  padding: ${theme.spacing['3xl']};
  text-align: center;
  box-shadow: 
    0 50px 100px rgba(0, 0, 0, 0.3),
    0 0 80px rgba(255, 215, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  max-width: 420px;
  width: 100%;
  position: relative;
  z-index: 100;
  animation: ${fadeInUp} 1.2s ease-out;
  
  /* 🎨 SOFISTIKE BORDER */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    background: ${theme.colors.gradient.primary};
    border-radius: 32px;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0.6;
    z-index: -1;
    animation: elegantBorder 8s ease-in-out infinite;
  }
  
  /* ✨ ZARIF İÇ EFEKTİ */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.colors.gradient.elegant};
    border-radius: 30px;
    opacity: 0.03;
    animation: subtleInnerGlow 10s ease-in-out infinite;
    z-index: -1;
  }
  
  /* İçerik z-index */
  & > * {
    position: relative;
    z-index: 1;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing['2xl']};
    margin: ${theme.spacing.lg};
  }
  
  @keyframes elegantBorder {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 0.8; }
  }
  
  @keyframes subtleInnerGlow {
    0%, 100% { opacity: 0.03; }
    50% { opacity: 0.08; }
  }
`;

const LogoContainer = styled.div`
  width: 80px;
  height: 80px;
  background: ${theme.colors.gradient.secondary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.xl};
  position: relative;
  box-shadow: 0 10px 30px rgba(139, 92, 246, 0.4);
  
  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    background: ${theme.colors.gradient.primary};
    border-radius: 50%;
    z-index: -1;
    opacity: 0.5;
    filter: blur(8px);
  }
`;

const WelcomeTitle = styled.h1`
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
  line-height: 1.2;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.fontSizes['2xl']};
  }
`;

const RestaurantName = styled.div`
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  background: ${theme.colors.gradient.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: ${theme.spacing.lg};
  letter-spacing: 0.5px;
`;

const WelcomeSubtitle = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing['2xl']};
  line-height: 1.5;
`;

const FeaturesList = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: ${theme.spacing['2xl']};
  gap: ${theme.spacing.md};
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex: 1;
`;

const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${theme.colors.glass.background};
  backdrop-filter: ${theme.colors.glass.backdrop};
  border: 1px solid ${theme.colors.glass.border};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary};
`;

const FeatureText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  text-align: center;
  font-weight: 500;
`;

const EnterButton = styled.button`
  background: ${theme.colors.gradient.primary};
  color: white;
  border: none;
  padding: ${theme.spacing.lg} ${theme.spacing['2xl']};
  border-radius: 50px;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.md};
  width: 100%;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(255, 0, 128, 0.4);
  z-index: 1000;
  outline: none;
  
  /* Touch için daha büyük tıklama alanı */
  &::after {
    content: '';
    position: absolute;
    inset: -10px;
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 15px 40px rgba(255, 0, 128, 0.6);
  }
  
  &:active {
    transform: translateY(-1px) scale(1.01);
    background: ${theme.colors.gradient.accent};
  }
  
  &:focus {
    outline: 2px solid ${theme.colors.accentColors.amber};
    outline-offset: 2px;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.colors.gradient.secondary};
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  /* İçerik z-index */
  & > * {
    position: relative;
    z-index: 2;
    pointer-events: none;
  }
`;

const SparkleIcon = styled(Sparkles)`
  position: absolute;
  color: white;
  opacity: 0.6;
  animation: ${sparkleAnimation} 3s ease-in-out infinite;
  
  &:nth-child(1) {
    top: 20%;
    left: 15%;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    top: 60%;
    right: 20%;
    animation-delay: 1s;
  }
  
  &:nth-child(3) {
    bottom: 30%;
    left: 25%;
    animation-delay: 2s;
  }
`;

export const WelcomeScreen = ({ restaurantName, onEnter }) => {
  // Sabit metin - değiştirilemez
  const SUBTITLE_TEXT = 'Lezzetli yemeklerimizi keşfetmek için menüyü inceleyin';
  
  const handleButtonClick = (e) => {
    console.log('Button clicked!', e);
    e.preventDefault();
    e.stopPropagation();
    if (onEnter) {
      onEnter();
    }
  };

  return (
    <WelcomeContainer>
      <BackgroundPattern />
      <SparkleIcon size={16} />
      <SparkleIcon size={20} />
      <SparkleIcon size={14} />
      
      <WelcomeCard>
        <LogoContainer>
          <ChefHat size={40} color="white" />
        </LogoContainer>
        
        <WelcomeTitle>Hoş Geldiniz!</WelcomeTitle>
        <RestaurantName>{restaurantName}</RestaurantName>
        <WelcomeSubtitle translate="no">
          {SUBTITLE_TEXT}
        </WelcomeSubtitle>
        
        <FeaturesList>
          <FeatureItem>
            <FeatureIcon>
              <Utensils size={24} />
            </FeatureIcon>
            <FeatureText>Taze Lezzetler</FeatureText>
          </FeatureItem>
          <FeatureItem>
            <FeatureIcon>
              <ChefHat size={24} />
            </FeatureIcon>
            <FeatureText>Ustaca Hazırlanmış</FeatureText>
          </FeatureItem>
          <FeatureItem>
            <FeatureIcon>
              <Sparkles size={24} />
            </FeatureIcon>
            <FeatureText>Özel Tarifler</FeatureText>
          </FeatureItem>
        </FeaturesList>
        
        <EnterButton onClick={handleButtonClick}>
          Menüyü Görüntüle
          <ArrowRight size={20} />
        </EnterButton>
      </WelcomeCard>
    </WelcomeContainer>
  );
};
